import api from '../Utils/api';
import { makeAutoObservable, runInAction } from "mobx";
import MediaSessionManager from '../Utils/MediaSessionManager';
import RecentUsersStore from "./RecentUsersStore";

class PlaybackStore {
  playlist = [];
  originalPlaylist = []; // Store original order for unshuffle
  currentPlaylistId = null;

  isPlaying = false;
  shuffleEnabled = false;

  // Debounce timer for saving position
  _saveDebounceTimer = null;

  constructor() {
    makeAutoObservable(this, {
      _saveDebounceTimer: false,
      originalPlaylist: false, // Don't make this observable
    });

    // Initialize Media Session API handlers for lock screen controls
    MediaSessionManager.setActionHandlers({
      play: () => this.play(),
      pause: () => this.pause(),
      previoustrack: () => this.previous(),
      nexttrack: () => this.next(),
      seekto: (details) => {
        if (details.seekTime !== undefined) {
          this.setCurrentTime(details.seekTime);
        }
      }
    });
  }

  setPlaylist(playlist, playlistId = null) {
    this.playlist = playlist;
    this.originalPlaylist = [...playlist]; // Save original order
    this.currentPlaylistId = playlistId;

    // Reset shuffle when playlist changes
    if (this.shuffleEnabled) {
      this.shuffleEnabled = false;
      this.saveToServer(true);
    }

    if (this.playlist.length > 0)
      this.currentTrackId = this.playlist[0].mediaId;
  }

  // Virtual playlist types that need special handling
  static VIRTUAL_PLAYLISTS = ['public', 'my-songs'];

  saveToServer(immediate = false) {
    // Debounce saves to avoid hammering the server
    if (this._saveDebounceTimer) {
      clearTimeout(this._saveDebounceTimer);
    }

    const doSave = () => {
      if (!this.currentTrackId) return;

      const audioElement = document.getElementById("DOMAudioPlayer");
      const positionMs = audioElement ? Math.floor(audioElement.currentTime * 1000) : 0;

      // Check if currentPlaylistId is a virtual playlist type (string) or regular numeric ID
      const isVirtualPlaylist = typeof this.currentPlaylistId === 'string' &&
        PlaybackStore.VIRTUAL_PLAYLISTS.includes(this.currentPlaylistId);

      const payload = {
        songMediaId: this.currentTrackId,
        positionMs,
        isPaused: !this.isPlaying,
        shuffleEnabled: this.shuffleEnabled,
      };

      if (isVirtualPlaylist) {
        payload.virtualPlaylistType = this.currentPlaylistId;
      } else {
        payload.playlistId = this.currentPlaylistId;
      }

      api.upsertPlaybackLast(payload).catch((e) => {
        console.error('Failed to save playback state:', e);
      });
    };

    if (immediate) {
      doSave();
    } else {
      this._saveDebounceTimer = setTimeout(doSave, 2000);
    }
  }

  async restoreFromServer() {
    try {
      const res = await api.getPlaybackLast();
      if (!res || Object.keys(res).length === 0) return;
      const { playlistId, virtualPlaylistType, songMediaId, positionMs, isPaused, shuffleEnabled } = res;

      // Helper to restore track and position after playlist is loaded
      const restoreTrackAndPosition = async (list, playlistIdentifier) => {
        runInAction(() => {
          this.originalPlaylist = [...list]; // Store original
          this.currentPlaylistId = playlistIdentifier;
          this.shuffleEnabled = shuffleEnabled || false;
        });

        // If shuffle was enabled, get a fresh shuffle from current song
        if (shuffleEnabled && songMediaId) {
          const params = { currentSongMediaId: songMediaId };
          if (typeof playlistIdentifier === 'string' &&
            PlaybackStore.VIRTUAL_PLAYLISTS.includes(playlistIdentifier)) {
            params.virtualPlaylistType = playlistIdentifier;
          } else {
            params.playlistId = playlistIdentifier;
          }

          try {
            const { list: shuffledList } = await api.getShuffledSongs(params);
            runInAction(() => {
              this.playlist = shuffledList;
              this.currentTrackId = songMediaId;
            });
          } catch (e) {
            // Fallback to original order
            runInAction(() => {
              this.playlist = list;
              if (songMediaId) {
                const track = list.find((t) => t.mediaId === songMediaId);
                if (track) this.currentTrackId = track.mediaId;
              }
            });
          }
        } else {
          runInAction(() => {
            this.playlist = list;
            if (songMediaId) {
              const track = list.find((t) => t.mediaId === songMediaId);
              if (track) this.currentTrackId = track.mediaId;
            }
          });
        }

        if (positionMs !== undefined && positionMs !== null) {
          this.setCurrentTime(positionMs / 1000);
        }
        if (isPaused === true || isPaused === 1) {
          this.isPlaying = false;
        }
      };

      // Fetch playlist data directly via API to avoid triggering preview mode
      if (virtualPlaylistType) {
        // Handle virtual playlists (public, my-songs)
        if (virtualPlaylistType === 'public') {
          const { list } = await api.getSongList();
          await restoreTrackAndPosition(list, 'public');
        } else if (virtualPlaylistType === 'my-songs') {
          // Need the current user ID to load user's songs
          if (RecentUsersStore.currentUser?.userId) {
            const { list } = await api.getUserSongs(RecentUsersStore.currentUser.userId);
            await restoreTrackAndPosition(list, 'my-songs');
          }
        }
      } else if (playlistId) {
        // Load regular playlist directly
        const { list } = await api.getSongList(playlistId);
        await restoreTrackAndPosition(list, playlistId);
      } else if (songMediaId) {
        // No playlist specified; try to map within current playlist
        runInAction(() => {
          const track = this.playlist.find((t) => t.mediaId === songMediaId);
          if (track) this.currentTrackId = track.mediaId;
        });
        if (positionMs !== undefined && positionMs !== null) {
          this.setCurrentTime(positionMs / 1000);
        }
      }
    } catch (e) {
      console.error('Failed to restore playback:', e);
    }
  }

  async toggleShuffle() {
    if (this.playlist.length === 0) return;

    try {
      const response = await api.toggleShuffle();

      runInAction(() => {
        this.shuffleEnabled = response.shuffleEnabled;
      });

      if (this.shuffleEnabled) {
        // Get shuffled order from server
        await this.applyShuffle();
      } else {
        // Restore original order
        this.restoreOriginalOrder();
      }
    } catch (e) {
      console.error('Failed to toggle shuffle:', e);
    }
  }

  async applyShuffle() {
    const isVirtualPlaylist = typeof this.currentPlaylistId === 'string' &&
      PlaybackStore.VIRTUAL_PLAYLISTS.includes(this.currentPlaylistId);

    const params = {
      currentSongMediaId: this.currentTrackId,
    };

    if (isVirtualPlaylist) {
      params.virtualPlaylistType = this.currentPlaylistId;
    } else if (this.currentPlaylistId) {
      params.playlistId = this.currentPlaylistId;
    }

    try {
      const { list } = await api.getShuffledSongs(params);
      runInAction(() => {
        this.playlist = list;
        // currentTrackId stays the same, it's now at index 0
      });
    } catch (e) {
      console.error('Failed to get shuffled songs:', e);
    }
  }

  restoreOriginalOrder() {
    // Restore original order but maintain current track playing
    runInAction(() => {
      this.playlist = [...this.originalPlaylist];
      // currentTrackId stays the same, the jump() method finds it by mediaId
    });
  }

  currentTrackId = null;

  get currentTrack() {
    return this.playlist.reduce(
      (acc, track, index) =>
        track.mediaId === this.currentTrackId ? { ...track, index } : acc,
      null
    );
  }

  togglePlay() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
    this.updateTitle();
  }

  play() {
    const audioElement = document.getElementById("DOMAudioPlayer");
    if (audioElement) {
      audioElement.play()
        .then(() => {
          this.isPlaying = true;
          MediaSessionManager.updatePlaybackState('playing');
          this.updateTitle();
          this.saveToServer();
        })
        .catch(err => {
          console.error('Playback failed:', err);
          this.isPlaying = false;
        });
    }
  }

  pause() {
    const audioElement = document.getElementById("DOMAudioPlayer");
    if (audioElement) {
      audioElement.pause();
      this.isPlaying = false;
      MediaSessionManager.updatePlaybackState('paused');
      this.updateTitle();
      this.saveToServer(true); // Save immediately on pause
    }
  }

  playTrack(trackId) {
    this.currentTrackId = trackId;

    // Update Media Session metadata for lock screen controls
    if (this.currentTrack) {
      MediaSessionManager.updateMetadata(this.currentTrack);
    }

    setTimeout(() => {
      this.play();
    });
    this.updateTitle();
    this.saveToServer(true); // Save immediately on track change
  }

  updateTitle() {
    document.getElementsByTagName("title")[0].innerText =
      this.isPlaying && this.currentTrack
        ? `▶ ${this.currentTrack.title} - ${this.currentTrack.artist} - Muzio`
        : "Muzio";
  }

  setCurrentTime(time) {
    const audioElement = document.getElementById("DOMAudioPlayer");
    if (audioElement) {
      audioElement.currentTime = time;
    }
  }

  jump(trackCount = 1) {
    try {
      const nextMediaId = this.playlist[this.currentTrack.index + trackCount]
        .mediaId;
      this.playTrack(nextMediaId);
    } catch (ex) {}
  }

  next() {
    this.jump(1);
  }

  previous() {
    this.jump(-1);
  }

  deleteSong(mediaId) {
    api.deleteSong(mediaId);
    this.next();
    this.playlist = this.playlist.filter((i) => i.mediaId !== mediaId);
  }
}

const playbackStore = new PlaybackStore();
export default playbackStore;
